import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, effect, inject, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { NotificationService } from '../services/notification.service';
import { AuthService } from '../auth/services/auth.service';
import { ConnectivityService } from './connectivity.service';
import { OfflineDbService, OutboxEntry } from './offline-db.service';

export type SyncState = 'idle' | 'syncing' | 'error';

/**
 * Dès que la connectivité réelle repasse à "online", rejoue dans l'ordre
 * les mutations stockées dans l'outbox IndexedDB (ventes, mouvements de
 * stock, paiements... créés hors-ligne), puis invalide le cache des
 * ressources concernées pour forcer un rafraîchissement propre.
 *
 * Chaque entrée porte un `localId` (UUID) : le composant appelant peut
 * donc afficher/manipuler l'enregistrement immédiatement (id optimiste),
 * la réconciliation avec l'id serveur se faisant silencieusement ici.
 */
@Injectable({ providedIn: 'root' })
export class SyncService {
  private readonly http = inject(HttpClient);
  private readonly db = inject(OfflineDbService);
  private readonly connectivity = inject(ConnectivityService);
  private readonly notifications = inject(NotificationService);
  private readonly auth = inject(AuthService);

  private readonly stateSignal = signal<SyncState>('idle');
  private readonly pendingCountSignal = signal(0);
  private readonly failedCountSignal = signal(0);

  readonly state = this.stateSignal.asReadonly();
  readonly pendingCount = this.pendingCountSignal.asReadonly();
  readonly failedCount = this.failedCountSignal.asReadonly();

  private syncing = false;
  private static readonly MAX_ATTEMPTS = 5;

  constructor() {
    this.refreshPendingCount();

    // Déclenche une synchro à chaque passage à "online".
    effect(() => {
      if (this.connectivity.status() === 'online') {
        void this.syncNow();
      }
    });
  }

  async refreshPendingCount(): Promise<void> {
    const scope = this.scope();
    if (!scope) {
      this.pendingCountSignal.set(0);
      this.failedCountSignal.set(0);
      return;
    }
    this.pendingCountSignal.set(await this.db.countPendingMutations(scope));
    this.failedCountSignal.set((await this.db.getFailedMutations(scope)).length);
  }

  async syncNow(): Promise<void> {
    if (this.syncing) {
      return;
    }
    const scope = this.scope();
    if (!scope) return;
    const pending = await this.db.getPendingMutations(scope);
    if (pending.length === 0) {
      this.stateSignal.set('idle');
      return;
    }

    this.syncing = true;
    this.stateSignal.set('syncing');
    let failures = 0;

    for (const entry of pending) {
      const ok = await this.replayEntry(entry);
      if (!ok) {
        failures += 1;
      }
    }

    await this.refreshPendingCount();
    this.syncing = false;
    this.stateSignal.set(failures > 0 ? 'error' : 'idle');

    if (failures === 0 && pending.length > 0) {
      this.notifications.success(
        `${pending.length} opération(s) hors-ligne synchronisée(s) avec succès.`,
      );
    } else if (failures > 0) {
      this.notifications.warning(
        `${failures} opération(s) n'ont pas pu être synchronisées et seront réessayées.`,
      );
    }
  }

  async failedMutations(): Promise<OutboxEntry[]> { const scope = this.scope(); return scope ? this.db.getFailedMutations(scope) : []; }
  async retryFailed(id: number): Promise<void> { const scope = this.scope(); if (!scope) return; await this.db.retryMutation(id, scope); await this.refreshPendingCount(); if (this.connectivity.status() === 'online') await this.syncNow(); }
  async discardFailed(id: number): Promise<void> { const scope = this.scope(); if (!scope) return; await this.db.removeMutation(id, scope); await this.refreshPendingCount(); }


  private extractIdempotencyKey(payload: unknown): string | null {
    if (typeof payload !== 'object' || payload === null) return null;

    const candidate = 'clientMutationId' in payload
      ? payload.clientMutationId
      : 'idempotencyKey' in payload
        ? payload.idempotencyKey
        : null;

    return typeof candidate === 'string' && candidate.trim() !== ''
      ? candidate.trim()
      : null;
  }

  private scope(): { organisationId: string; userId: string } | null {
    const user = this.auth.currentUser();
    return user ? { organisationId: user.organisationId, userId: user.id } : null;
  }

  private async replayEntry(entry: OutboxEntry): Promise<boolean> {
    const url = entry.path
      ? `${environment.apiUrl}/${entry.path}`
      : entry.targetId
        ? `${environment.apiUrl}/${entry.resource}/${entry.targetId}`
        : `${environment.apiUrl}/${entry.resource}`;

    const isCustomAction = entry.path !== undefined;
    const apiPlatformHeaders = new HttpHeaders({
      Accept: 'application/ld+json, application/json',
      'Content-Type': 'application/ld+json',
    });
    const mergePatchHeaders = new HttpHeaders({
      Accept: 'application/ld+json, application/json',
      'Content-Type': 'application/merge-patch+json',
    });

    const idempotencyKey = this.extractIdempotencyKey(entry.payload);
    const postHeaders = (isCustomAction ? new HttpHeaders() : apiPlatformHeaders)
      .set('Idempotency-Key', idempotencyKey ?? '');
    const postOptions = idempotencyKey
      ? { headers: postHeaders }
      : isCustomAction
        ? undefined
        : { headers: apiPlatformHeaders };

    try {
      switch (entry.method) {
        case 'POST':
          await this.http
            .post(url, entry.payload, postOptions)
            .toPromise();
          break;
        case 'PATCH':
          await this.http.patch(url, entry.payload, { headers: mergePatchHeaders }).toPromise();
          break;
        case 'PUT':
          await this.http.put(url, entry.payload, { headers: apiPlatformHeaders }).toPromise();
          break;
        case 'DELETE':
          await this.http
            .delete(url, {
              headers: new HttpHeaders({ Accept: 'application/ld+json, application/json' }),
            })
            .toPromise();
          break;
      }

      const scope = this.scope();
      if (!scope || entry.organisationId !== scope.organisationId || entry.userId !== scope.userId) {
        return false;
      }
      await this.db.invalidateResourceCache(entry.resource, scope);
      if (entry.id !== undefined) {
        await this.db.removeMutation(entry.id, scope);
      }
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur inconnue';
      if (entry.id !== undefined) {
        const scope = this.scope();
        if (!scope) return false;
        await this.db.touchMutationAttempt(entry.id, message, scope);
        // Au-delà du nombre max de tentatives, on abandonne pour ne pas
        // bloquer indéfiniment la file (l'utilisateur reste notifié).
        if (entry.attempts + 1 >= SyncService.MAX_ATTEMPTS) {
          this.notifications.error(
            `Une opération hors-ligne (${entry.resource}) nécessite une intervention. Elle a été conservée dans la file d'échecs.`,
          );
          await this.db.markMutationFailed(entry.id, message, scope);
        }
      }
      return false;
    }
  }
}
